import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { BannerBar } from '../components/BannerBar';
import { useTheme } from '../contexts/ThemeContext';
import { formatDate, STATUS_DISPLAY_NAMES } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { EngineerJobDetail } from '../components/EngineerJobDetail';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Calendar,
  MapPin,
  Phone,
  Mail,
  Clock,
  User,
  FileText,
  ChevronRight,
  CheckCircle,
  Loader2,
  Upload,
  LogOut,
  Wrench,
  Sun,
  Moon,
  Home,
  ClipboardCheck,
  Camera,
  Navigation,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const LOGO_WHITE = 'https://customer-assets.emergentagent.com/job_electrical-tracker-1/artifacts/8ty3m189_Updated%20Logo%20White.png';

export const EngineerApp = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [installations, setInstallations] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadNotes, setUploadNotes] = useState('');
  const [view, setView] = useState('jobs'); // jobs | detail
  const [detailRefresh, setDetailRefresh] = useState(0);

  useEffect(() => {
    const engToken = localStorage.getItem('engineer_token');
    if (!engToken) { navigate('/engineer-login'); return; }
    const stored = localStorage.getItem('engineer_user');
    if (stored) setUser(JSON.parse(stored));
    fetchJobs();

    // Register PWA service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/engineer-sw.js').catch(() => {});
    }

    // Add manifest link for PWA
    let link = document.querySelector('link[rel="manifest"][data-engineer]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/engineer-manifest.json';
      link.setAttribute('data-engineer', 'true');
      document.head.appendChild(link);
    }
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#0f172a');
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const meRes = await api.get('/engineer-auth/me');
      setUser(meRes.data);

      const res = await api.get('/engineer/my-installations');
      setInstallations(res.data || []);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('engineer_token');
        navigate('/engineer-login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewJob = (installation) => {
    setSelectedJob(installation);
    setView('detail');
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedJob) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('notes', uploadNotes);
      await api.post(`/engineer/job/${selectedJob.customer_id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded to the customer file');
      setUploadDialog(false);
      setUploadNotes('');
      setDetailRefresh((n) => n + 1);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('engineer_token');
    localStorage.removeItem('engineer_user');
    navigate('/engineer-login');
  };

  const openMaps = (address) => {
    window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
  };

  const callPhone = (phone) => {
    window.location.href = `tel:${phone}`;
  };

  const today = new Date().toISOString().split('T')[0];
  const todayJobs = installations.filter(i => i.scheduled_date === today);
  const upcomingJobs = installations.filter(i => i.scheduled_date > today);
  const pastJobs = installations.filter(i => i.scheduled_date < today);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 pb-20" data-testid="engineer-app">
      {/* Fixed header */}
      <header className="sticky top-0 z-50 bg-slate-900 text-white shadow-lg safe-area-top">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <img src={LOGO_WHITE} alt="Helios" className="h-8" onError={(e) => { e.target.style.display = 'none'; }} />
            {view === 'detail' && (
              <Button variant="ghost" size="sm" className="text-white hover:bg-slate-800" onClick={() => setView('jobs')}>
                Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme} className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 w-8" data-testid="eng-theme-toggle">
              {theme === 'dark' ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-slate-300 hover:text-white hover:bg-slate-800 h-8 w-8" data-testid="eng-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <BannerBar endpoint="/banners/engineer" />

      {view === 'jobs' ? (
        <div className="px-4 py-4 max-w-lg mx-auto space-y-6">
          {/* Welcome */}
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white font-['Manrope']">
              Hi, {user?.name?.split(' ')[0] || 'Engineer'}
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">{formatDate(today)}</p>
          </div>

          {/* Today's Jobs */}
          <div>
            <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Today ({todayJobs.length})
            </h2>
            {todayJobs.length === 0 ? (
              <Card className="border-slate-200 dark:border-slate-700">
                <CardContent className="py-8 text-center text-slate-400">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                  <p className="text-sm">No jobs scheduled for today</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {todayJobs.map((job) => (
                  <JobCard key={job.id} job={job} onView={() => handleViewJob(job)} onNavigate={() => openMaps(job.customer_address)} />
                ))}
              </div>
            )}
          </div>

          {/* Upcoming */}
          {upcomingJobs.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                Upcoming ({upcomingJobs.length})
              </h2>
              <div className="space-y-3">
                {upcomingJobs.slice(0, 10).map((job) => (
                  <JobCard key={job.id} job={job} onView={() => handleViewJob(job)} onNavigate={() => openMaps(job.customer_address)} compact />
                ))}
              </div>
            </div>
          )}

          {/* Past */}
          {pastJobs.length > 0 && (
            <div>
              <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">
                Completed ({pastJobs.length})
              </h2>
              <div className="space-y-2">
                {pastJobs.slice(0, 5).map((job) => (
                  <JobCard key={job.id} job={job} onView={() => handleViewJob(job)} compact past />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Job Detail View */
        <div className="px-4 py-4 max-w-lg mx-auto" data-testid="engineer-job-detail">
          {selectedJob && (
            <EngineerJobDetail job={selectedJob} refreshKey={detailRefresh} onUploadPhoto={() => setUploadDialog(true)} />
          )}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Notes (optional)</Label>
              <Textarea
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="Description of the photo..."
                rows={2}
              />
            </div>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleUploadPhoto}
              className="w-full text-sm"
              data-testid="eng-photo-input"
            />
            {uploading && (
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                Uploading...
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Job Card Component
const JobCard = ({ job, onView, onNavigate, compact, past }) => (
  <Card
    className={`border-slate-200 dark:border-slate-700 cursor-pointer active:scale-[0.98] transition-transform ${past ? 'opacity-70' : ''}`}
    onClick={onView}
    data-testid={`job-card-${job.id}`}
  >
    <CardContent className={compact ? 'py-3 px-4' : 'py-4 px-4'}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 dark:text-white text-sm truncate">{job.customer_name}</p>
          <div className="flex items-center gap-2 mt-1">
            <MapPin className="w-3 h-3 text-slate-400 flex-shrink-0" />
            <p className="text-xs text-slate-500 truncate">{job.customer_address}</p>
          </div>
          {!compact && (
            <>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-3 h-3 text-slate-400" />
                <span className="text-xs text-slate-500">{job.start_time} - {job.end_time}</span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 truncate">{job.title}</p>
            </>
          )}
          {compact && (
            <div className="flex items-center gap-2 mt-1">
              <Calendar className="w-3 h-3 text-slate-400" />
              <span className="text-xs text-slate-500">{formatDate(job.scheduled_date)}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 ml-3">
          {!past && onNavigate && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={(e) => { e.stopPropagation(); onNavigate(); }}
              data-testid={`navigate-btn-${job.id}`}
            >
              <Navigation className="w-4 h-4 text-blue-500" />
            </Button>
          )}
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const Label = ({ children, className = '' }) => (
  <label className={`block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1 ${className}`}>{children}</label>
);
